import { DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { useSmartSensors } from '@framework/ui/core/dnd/hooks/useSmartSensors.tsx';
import { useCallback } from 'react';
import { SortableAttributeRow } from './SortableAttributeRow.tsx';

export interface ISortableAttributeListProps {
  /** Prefisso del campo form (es. "layers.0.intersection.") */
  prefix: string;
  /** Lista degli attributi da visualizzare e riordinare */
  attributes: string[];
}

/**
 * Lista di attributi riordinabile via drag-and-drop.
 * Ogni riga mostra un handle di trascinamento e il campo alias.
 * Al termine del drag, aggiorna l'ordine dell'array attributes nel form.
 */
export function SortableAttributeList(props: ISortableAttributeListProps) {
  const { prefix, attributes } = props;
  const setValue = useFormSetValue();
  const sensors = useSmartSensors();

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = attributes.indexOf(String(active.id));
      const newIndex = attributes.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        setValue(
          `${prefix}attributes`,
          arrayMove(attributes, oldIndex, newIndex),
        );
      }
    },
    [attributes, prefix, setValue],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={attributes}
        strategy={verticalListSortingStrategy}
      >
        {attributes.map(attribute => (
          <SortableAttributeRow
            key={attribute}
            attribute={attribute}
            prefix={prefix}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
